'use client';

import { useDeviceName } from 'lib/api/myRobot';

import { KeyPathParser, useTwinQuerySub } from 'hooks/signalR';

import { BatteryState } from 'lib/generated/proto/sensor_msgs/msg/BatteryState';
import { Location } from 'lib/generated/proto/robot_interfaces/msg/Location';
import { type RobotState } from 'lib/generated/proto/robot_interfaces/msg/RobotState';

/**
 * Subscribe to the full RobotState for the current robot.
 * Key: `${deviceName}:Reported:State`
 */
export function useRobotState() {
  const { deviceName } = useDeviceName();

  const { data } = useTwinQuerySub<RobotState>(
    KeyPathParser.RobotState.keyPath(deviceName),
    KeyPathParser.RobotState.parser,
  );

  return { robotState: data };
}

/**
 * Subscribe to just the armed flag from OperationMode.
 * Path: `${deviceName}:Reported:State` -> `$.operationMode.armed`
 */
export function useIsArmed() {
  const { deviceName } = useDeviceName();

  const { data } = useTwinQuerySub<boolean>(
    KeyPathParser.RobotState.isArmed.keyPath(deviceName),
    KeyPathParser.RobotState.isArmed.parser,
  );

  return { isArmed: data ?? false };
}

/**
 * Subscribe to the robot's location (GPS + pose).
 * Key: `${deviceName}:Reported:Location`
 */
export function useRobotLocation() {
  const { deviceName } = useDeviceName();

  const { data: location } = useTwinQuerySub<Location>(
    KeyPathParser.RobotLocation.keyPath(deviceName),
    KeyPathParser.RobotLocation.parser,
  );

  const hasLocation =
    location?.gps !== undefined &&
    (location.gps.latitude !== 0 || location.gps.longitude !== 0);

  return { location, hasLocation };
}

/**
 * Subscribe to the robot's battery state from RobotState.
 * Path: `${deviceName}:Reported:State` -> `$.battery`
 */
export function useRobotBattery() {
  const { deviceName } = useDeviceName();

  const { data: battery } = useTwinQuerySub<BatteryState>(
    KeyPathParser.RobotState.battery.keyPath(deviceName),
    KeyPathParser.RobotState.battery.parser,
  );

  return { battery };
}
